/**
 * Shared-session lifecycle across browser contexts (ADR-0039 amendments 2–5; Codex rounds 2–5).
 *
 * Every tab of a console origin shares ONE session in `localStorage`. Establishing and ending it both run under the
 * cross-context session lock the refresh uses (`SESSION_REFRESH_LOCK`) and follow one logout-epoch protocol (each logout
 * publishes a fresh random epoch; epochs are compared by equality only):
 *
 *  - **Ending** ({@link logoutSession}, {@link signOutIfIdle}): re-read the shared session → publish a fresh epoch FIRST
 *    (the tombstone: an in-flight login or refresh from before can no longer commit) → a bounded server revocation with
 *    the REFRESH credential (works after the access token expired) → clear ONLY the session that was being ended (an older
 *    logout never erases a newer login) → if the tombstone write was refused, retry it now that the removal freed space,
 *    else retire the epoch by removal → signal every tab. The lock is never released while a pre-logout login can commit.
 *  - **Establishing** ({@link establishSharedSession}): capture the epoch under the lock BEFORE the login request (an
 *    absent epoch is initialized first, so a captured epoch is always present), mint the session outside the lock
 *    (optionally staging it in this tab's memory to call `/v1/me`), then commit under the lock only if the epoch is
 *    unchanged; if a logout won, revoke the freshly minted session server-side and commit nothing.
 *  - **Inactivity** is the latest activity of ANY tab: every tab publishes its own timestamp key and the decision takes
 *    the maximum, so an interleaved older write can never lower another tab's activity ({@link createSharedActivity}).
 */
import { uuidv7 } from '@vello/core';
import { resolveLocalStorage } from './web-secure-store.js';
import { SESSION_REFRESH_LOCK } from './session-refresh.js';
// ---- shared activity (per-tab marks, max at decision time) -------------------------------------------------
/** Prefix of the per-tab activity keys (`vello.session.activity.<tabId>`). Timestamps only — no secret. */
export const SHARED_ACTIVITY_KEY = 'vello.session.activity';
export const DEFAULT_SERVER_LOGOUT_TIMEOUT_MS = 3_000;
const DEFAULT_STALE_ACTIVITY_MS = 24 * 60 * 60 * 1000;
const ms = (iso) => (iso === undefined || iso === null ? Number.NaN : Date.parse(iso));
const randomTabId = () => globalThis.crypto?.randomUUID?.() ?? uuidv7();
export function createSharedActivity(options = {}) {
    const storage = options.storage === undefined ? resolveLocalStorage() : (options.storage ?? undefined);
    const prefix = `${options.key ?? SHARED_ACTIVITY_KEY}.`;
    const ownKey = `${prefix}${options.tabId ?? randomTabId()}`;
    const minWriteIntervalMs = options.minWriteIntervalMs ?? 5_000;
    const staleAfterMs = options.staleAfterMs ?? DEFAULT_STALE_ACTIVITY_MS;
    let local;
    let lastWriteMs = Number.NEGATIVE_INFINITY;
    /** Every published mark: (key, value) of each `prefix*` entry, or just this tab's when storage cannot enumerate. */
    const marks = () => {
        const out = [];
        try {
            if (storage === undefined)
                return out;
            const n = storage.length;
            if (typeof n === 'number' && typeof storage.key === 'function') {
                const keys = [];
                for (let i = 0; i < n; i += 1) {
                    const k = storage.key(i);
                    if (k !== null && k.startsWith(prefix))
                        keys.push(k);
                }
                for (const k of keys) {
                    const v = storage.getItem(k);
                    if (v !== null)
                        out.push([k, v]);
                }
            }
            else {
                const own = storage.getItem(ownKey);
                if (own !== null)
                    out.push([ownKey, own]);
            }
        }
        catch {
            /* storage unavailable: activity is tab-local (and so is the session store) */
        }
        return out;
    };
    return {
        mark(at) {
            const atMs = ms(at);
            if (Number.isNaN(atMs))
                return;
            if (local === undefined || atMs > ms(local))
                local = at;
            if (atMs - lastWriteMs < minWriteIntervalMs)
                return; // throttled, and never publishes an older mark
            try {
                storage?.setItem(ownKey, at); // only this tab writes this key: no cross-tab read/compare/write race
                lastWriteMs = atMs;
                for (const [k, v] of marks())
                    if (k !== ownKey && ms(v) < atMs - staleAfterMs)
                        storage?.removeItem(k);
            }
            catch {
                /* tab-local only */
            }
        },
        latest(localAt) {
            let best = localAt;
            for (const candidate of [local, ...marks().map(([, v]) => v)]) {
                if (candidate !== undefined && !Number.isNaN(ms(candidate)) && (Number.isNaN(ms(best)) || ms(candidate) > ms(best)))
                    best = candidate;
            }
            return best;
        },
    };
}
async function bounded(task, timeoutMs) {
    let timer;
    try {
        const run = Promise.resolve().then(task).catch(() => undefined);
        await Promise.race([run, new Promise((resolve) => { timer = setTimeout(resolve, timeoutMs); })]);
    }
    finally {
        if (timer !== undefined)
            clearTimeout(timer);
    }
}
/**
 * Inside the lock, with the shared session already re-read: tombstone FIRST, then a bounded server revocation with the
 * refresh credential (always attempted — it does not depend on the access token), then clear only this session, signal.
 *
 * Amendment 5 (Codex r5 P1): the lock is never released after an unacknowledged tombstone in a state a pre-logout sign-in
 * could commit into. A refused epoch write (e.g. an exhausted quota) is retried once the session removal has freed space;
 * if the shared store still refuses writes, the epoch is retired by removal — every sign-in captured a PRESENT epoch, so the
 * pending sign-in finds it changed, revokes what it minted and commits nothing.
 */
async function endSharedSession(input) {
    const store = input.sessionStore;
    const expectedSessionId = store.snapshot().principal?.sessionId;
    const refreshToken = store.refreshToken();
    const tombstoned = (await store.bumpLogoutGeneration()) !== undefined;
    if (refreshToken !== undefined) {
        await bounded(async () => {
            const deviceFingerprint = await input.deviceFingerprint();
            await input.auth.revokeSession({ refreshToken, deviceFingerprint });
        }, input.serverTimeoutMs ?? DEFAULT_SERVER_LOGOUT_TIMEOUT_MS);
    }
    await store.clearIfCurrent(expectedSessionId);
    if (!tombstoned && (await store.bumpLogoutGeneration()) === undefined)
        await store.retireLogoutGeneration();
    input.signal?.notify();
}
/**
 * End the shared session only if it is REALLY due: under the session lock, re-read the shared session and every tab's
 * activity; keep it when any tab was active within `inactivityMs` and the (re-read) access token has not expired.
 */
export async function signOutIfIdle(input) {
    const run = async () => {
        const state = await input.sessionStore.load();
        if (state.status !== 'authenticated')
            return 'already-signed-out';
        const idleMs = Date.parse(input.now) - Date.parse(input.activity.latest(input.localActivityAt));
        const idle = Number.isNaN(idleMs) || idleMs >= input.inactivityMs;
        const expired = input.sessionStore.needsRefresh(input.now, 0);
        if (!idle && !expired)
            return 'kept';
        await endSharedSession({
            sessionStore: input.sessionStore, auth: input.auth, deviceFingerprint: input.deviceFingerprint,
            ...(input.signal !== undefined ? { signal: input.signal } : {}),
            ...(input.serverTimeoutMs !== undefined ? { serverTimeoutMs: input.serverTimeoutMs } : {}),
        });
        return 'signed-out';
    };
    return input.lock !== undefined ? input.lock.runExclusive(input.lockName ?? SESSION_REFRESH_LOCK, run) : run();
}
/** An explicit logout: under the session lock, tombstone, revoke server-side (bounded), clear this session, signal. */
export async function logoutSession(input) {
    const run = async () => {
        await input.sessionStore.load(); // the CURRENT shared session (another tab may have rotated it)
        await endSharedSession({
            sessionStore: input.sessionStore, auth: input.auth, deviceFingerprint: input.deviceFingerprint,
            ...(input.signal !== undefined ? { signal: input.signal } : {}),
            ...(input.serverTimeoutMs !== undefined ? { serverTimeoutMs: input.serverTimeoutMs } : {}),
        });
    };
    await (input.lock !== undefined ? input.lock.runExclusive(input.lockName ?? SESSION_REFRESH_LOCK, run) : run());
}
// ---- establishing the shared session ------------------------------------------------------------------------
/**
 * Establish a session inside the logout-generation protocol (Codex round 3 P1-2). `mint` performs the login (and may
 * `sessionStore.stage(...)` the new bearer in this tab's memory to call `/v1/me`); it returns the session to commit. The
 * epoch is captured BEFORE `mint` — under the session lock, and always PRESENT in shared storage (amendment 5) — and the
 * commit runs under the session lock only if no logout happened meanwhile; otherwise the freshly minted session is revoked
 * server-side (bounded) and nothing is committed. `mint` itself stays OUTSIDE the lock, so a logout requested while the
 * login is on the network wins instead of queueing behind it.
 */
export async function establishSharedSession(input) {
    const store = input.sessionStore;
    // Amendment 5 (Codex r5 P1): capture under the lock — linearized with every logout and refresh, so initializing an absent
    // epoch never lands inside another context's critical section — and make the epoch PRESENT, so a logout whose epoch
    // write is refused can still retire it by removal.
    const capture = () => store.captureLogoutGeneration();
    const generation = await (input.lock !== undefined ? input.lock.runExclusive(input.lockName ?? SESSION_REFRESH_LOCK, capture) : capture());
    if (generation === undefined) {
        // Amendment 4 (Codex r4 P1-1): the shared epoch cannot be verified (or initialized), so a logout elsewhere could not be
        // detected — fail closed before minting anything.
        await store.load();
        return { ok: false, error: { code: 'SERVICE_UNAVAILABLE', status: 503, detail: 'Session storage is unavailable, so sign-in cannot complete safely.' } };
    }
    let minted;
    try {
        minted = await input.mint();
    }
    catch (e) {
        await store.load(); // drop anything staged
        throw e;
    }
    if (!minted.ok) {
        await store.load(); // fail closed: a staged session is discarded, nothing persisted
        return minted;
    }
    const { session, realm } = minted.value;
    const revokeMinted = () => bounded(() => input.auth.revokeSession({ refreshToken: session.refreshToken, deviceFingerprint: input.deviceFingerprint }), input.serverTimeoutMs ?? DEFAULT_SERVER_LOGOUT_TIMEOUT_MS);
    const commit = async () => {
        // An unverifiable epoch (undefined) never equals the captured one: fail closed exactly like a logout that won.
        if ((await store.logoutGeneration()) !== generation) {
            await revokeMinted();
            await store.load();
            return { ok: false, error: { code: 'UNAUTHORIZED', status: 401, detail: 'A sign-out happened while signing in. Sign in again.' } };
        }
        // The commit itself must be acknowledged by the shared store: a session only this tab's memory holds would evade every
        // other tab's logout protocol (Codex r4 P1-1).
        const state = await store.commitSignIn(session, realm);
        if (state === undefined) {
            await revokeMinted();
            await store.load();
            return { ok: false, error: { code: 'SERVICE_UNAVAILABLE', status: 503, detail: 'Session storage is unavailable, so sign-in cannot complete safely.' } };
        }
        input.signal?.notify();
        return { ok: true, value: state };
    };
    return input.lock !== undefined ? input.lock.runExclusive(input.lockName ?? SESSION_REFRESH_LOCK, commit) : commit();
}
//# sourceMappingURL=session-lifecycle.js.map